import { useEffect, useMemo, useState } from 'react'
import {
    AdvancedMarker,
    AdvancedMarkerAnchorPoint,
    Polygon,
    useMap,
} from '@vis.gl/react-google-maps'

import { colorForGrower } from './area-map-colors'
import { MIN_LABEL_ZOOM, MIN_LOAD_ZOOM, tilesForBounds } from './area-map-tiles'
import { polylabel } from './polylabel'
import { normalizeGeo } from './use-area-polygon'
import type { Bounds } from './area-map-tiles'
import type { AreaMapItem } from '@/lib/api/grower-area-map-api'
import { useAreaMapTiles } from '@/hooks/use-area-map-query'
import { useAuth } from '@/hooks/use-auth'
import { m } from '@/lib/paraglide/messages'

type View = { bounds: Bounds; zoom: number }

/**
 * Khung nhìn hiện tại của `<Map>` bao quanh, đọc ở sự kiện `idle` (bản đồ đã
 * dừng kéo/zoom) chứ không phải `bounds_changed` — sự kiện kia bắn liên tục
 * khi kéo, mỗi lần là một lượt tính ô + render lại.
 */
export function useMapViewport(): View | null {
    const map = useMap()
    const [view, setView] = useState<View | null>(null)
    useEffect(() => {
        if (!map) return
        const read = () => {
            const b = map.getBounds()
            const zoom = map.getZoom()
            if (!b || zoom == null) return
            const ne = b.getNorthEast()
            const sw = b.getSouthWest()
            setView({
                bounds: {
                    north: ne.lat(),
                    south: sw.lat(),
                    east: ne.lng(),
                    west: sw.lng(),
                },
                zoom,
            })
        }
        const l = map.addListener('idle', read)
        read()
        return () => l.remove()
    }, [map])
    return view
}

export interface AreaMapOverlayProps {
    /**
     * `true` (màn bản đồ chung): click thửa/nhãn → `onSelect`.
     * `false` (màn hộ): thửa là NỀN THAM KHẢO — không bắt click, để click bản
     * đồ vẫn là "thêm mốc ranh giới" cho thửa đang vẽ.
     */
    interactive?: boolean
    selectedId?: string | null
    onSelect?: (area: AreaMapItem) => void
    /**
     * Thửa trong DB của hộ này bị ẩn: form đang sửa cung cấp bản "sống" của
     * chúng qua `siblings` (kể cả vùng vừa thêm chưa lưu / đã đổi toạ độ).
     */
    excludeGrowerId?: string
    /** Thửa đang vẽ ở lớp trên (polygon đỏ) — không vẽ đè bằng lớp nền. */
    excludeAreaIds?: string[]
    /** Thửa khác của hộ đang sửa, lấy từ form (không phải DB). */
    siblings?: AreaMapItem[]
    /** Zoom giả định trước khi `idle` đầu tiên bắn (để quyết định tải/nhãn). */
    initialZoom: number
    /** Dải nhắc "phóng to" / "đang tải" / lỗi. Màn hộ tắt để không che thửa. */
    showBanners?: boolean
}

/**
 * Lớp vẽ mọi thửa của mọi hộ lên `<Map>` bao quanh: tải theo ô lưới đang nhìn
 * (lazy-load, cache tới khi reload trang), cùng hộ cùng màu, nhãn "hộ / vùng"
 * tại điểm sâu nhất trong thửa. Dùng chung cho màn bản đồ chung và bản đồ
 * trong tab vùng trồng của hộ.
 */
export function AreaMapOverlay({
    interactive = false,
    selectedId = null,
    onSelect,
    excludeGrowerId,
    excludeAreaIds,
    siblings,
    initialZoom,
    showBanners = false,
}: AreaMapOverlayProps) {
    const view = useMapViewport()
    // /grower-areas/map đòi growers.read. Tài khoản hộ dân (role grower) không
    // có quyền này nhưng vẫn mở bản đồ của chính họ (my-grower, nhật ký) →
    // không gọi API để khỏi bắn 403 theo từng ô; thửa của chính hộ (siblings)
    // vẫn vẽ vì lấy từ dữ liệu họ được xem.
    const { hasPermission } = useAuth()
    const canLoadNeighbors = hasPermission('growers.read')
    const zoom = view?.zoom ?? initialZoom
    const zoomTooFar = zoom < MIN_LOAD_ZOOM
    const tiles = useMemo(
        () =>
            view && !zoomTooFar && canLoadNeighbors
                ? tilesForBounds(view.bounds)
                : [],
        [view, zoomTooFar, canLoadNeighbors],
    )
    // Khung nhìn quá rộng (màn 4K ở zoom 12) vượt MAX_TILES_PER_VIEW → không
    // tải; phải hiện cùng lời nhắc "phóng to" chứ không được im lặng trống.
    const tooFar =
        zoomTooFar || (view != null && canLoadNeighbors && tiles.length === 0)
    const { items, isLoading, isError } = useAreaMapTiles(tiles)

    const visible = useMemo(() => {
        const excluded = new Set(excludeAreaIds ?? [])
        const fromDb = items.filter(
            (a) =>
                !excluded.has(a.area_id) &&
                (excludeGrowerId == null || a.grower_id !== excludeGrowerId),
        )
        const extra = (siblings ?? []).filter((a) => !excluded.has(a.area_id))
        // Thửa của hộ đang sửa lấy từ form là nguồn chân lý; bản DB (nếu có
        // trong ô đã tải) đã bị loại ở trên theo grower_id.
        return [...fromDb, ...extra]
    }, [items, siblings, excludeAreaIds, excludeGrowerId])

    return (
        <>
            {visible.map((a) => (
                <AreaPolygon
                    key={a.area_id}
                    area={a}
                    interactive={interactive}
                    selected={a.area_id === selectedId}
                    showLabel={zoom >= MIN_LABEL_ZOOM}
                    onSelect={onSelect ? () => onSelect(a) : undefined}
                />
            ))}
            {showBanners && tooFar && <Banner>{m.area_map_zoom_hint()}</Banner>}
            {showBanners && !tooFar && isLoading && (
                <Banner>{m.area_map_loading()}</Banner>
            )}
            {showBanners && isError && (
                <Banner tone="error">{m.area_map_load_error()}</Banner>
            )}
        </>
    )
}

function AreaPolygon({
    area,
    interactive,
    selected,
    showLabel,
    onSelect,
}: {
    area: AreaMapItem
    interactive: boolean
    selected: boolean
    showLabel: boolean
    onSelect?: () => void
}) {
    const geo = useMemo(() => normalizeGeo(area.paths), [area.paths])
    const color = colorForGrower(area.grower_id)
    const labelAt = useMemo(() => polylabel(geo.paths), [geo.paths])
    // Dưới 3 đỉnh không thành thửa: không vẽ, không nhãn (API đã lọc vùng chưa
    // khoanh, nhưng dữ liệu 1–2 đỉnh vẫn có thể lọt).
    if (geo.paths.length < 3) return null

    return (
        <>
            <Polygon
                paths={geo.paths}
                fillColor={color}
                fillOpacity={selected ? 0.5 : interactive ? 0.3 : 0.18}
                strokeColor={color}
                strokeOpacity={interactive ? 1 : 0.8}
                strokeWeight={selected ? 4 : 2}
                clickable={interactive}
                onClick={interactive ? onSelect : undefined}
            />
            {showLabel && (
                <AdvancedMarker
                    position={labelAt}
                    zIndex={selected ? 2 : 1}
                    anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
                    // gmp-advanced-marker luôn nuốt click (pointer-events của
                    // nội dung không ngăn được) → ở màn chung cho chính nhãn
                    // chọn thửa; ở màn hộ không gắn gì (nhãn chỉ để đọc).
                    onClick={interactive ? onSelect : undefined}
                >
                    <AreaLabel
                        area={area}
                        color={color}
                        interactive={interactive}
                    />
                </AdvancedMarker>
            )}
        </>
    )
}

/**
 * Nhãn 2 dòng nền màu hộ, neo ở tâm (điểm sâu nhất trong thửa):
 *   dòng 1 — tên hộ, kèm "(tên thường gọi)" nếu có;
 *   dòng 2 — tên vùng · mã vùng (vùng chưa lưu thì chưa có mã → chỉ tên).
 */
export function AreaLabel({
    area,
    color,
    interactive = true,
}: {
    area: AreaMapItem
    color: string
    interactive?: boolean
}) {
    const nick = area.grower_nickname?.trim()
    const grower = nick ? `${area.grower_name} (${nick})` : area.grower_name
    return (
        <div
            data-testid="area-label"
            className={
                'rounded-md px-2.5 py-1.5 text-center text-[13px] leading-snug text-white shadow-md ' +
                (interactive ? 'cursor-pointer' : 'opacity-90')
            }
            style={{ backgroundColor: color, maxWidth: 200 }}
        >
            <div className="truncate font-semibold">{grower}</div>
            <div className="truncate opacity-90">
                {area.code ? `${area.name} · ${area.code}` : area.name}
            </div>
        </div>
    )
}

export function Banner({
    children,
    tone = 'info',
}: {
    children: React.ReactNode
    tone?: 'info' | 'error'
}) {
    return (
        <div
            role="status"
            className={
                'pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-md px-3 py-1.5 text-xs shadow ' +
                (tone === 'error'
                    ? 'bg-destructive text-white'
                    : 'bg-black/70 text-white')
            }
        >
            {children}
        </div>
    )
}
